<div class="timeline-container">
  <!-- Loading State with Progress -->
  <div *ngIf="isLoading || isRendering" class="loading-state">
    <div class="progress-container">
      <div class="progress-header">
        <h2>Loading Family Timeline</h2>
        <div class="progress-stats" *ngIf="loadingProgress.total > 0">
          {{ loadingProgress.loaded }} / {{ loadingProgress.total }} events
        </div>
        <div class="progress-stats" *ngIf="loadingProgress.total === 0">
          Preparing...
        </div>
      </div>
      
      <div class="progress-bar-wrapper">
        <div class="progress-bar">
          <div class="progress-fill" [style.width.%]="loadingProgress.percentage"></div>
        </div>
        <div class="progress-percentage">{{ loadingProgress.percentage }}%</div>
      </div>
      
      <div class="progress-message">
        <span class="progress-icon">📅</span>
        <span>{{ loadingProgress.message }}</span>
      </div>
      
      <!-- Level Progress -->
      <div class="level-progress" *ngIf="loadingProgress.levelProgress.total > 0">
        <div class="level-progress-bar">
          <div class="level-progress-fill" 
               [style.width.%]="(loadingProgress.levelProgress.current / loadingProgress.levelProgress.total) * 100">
          </div>
        </div>
        <div class="level-progress-text">
          Level {{ loadingProgress.levelProgress.current }} of {{ loadingProgress.levelProgress.total }}
          <span class="current-level" *ngIf="loadingProgress.currentLevel">
            • {{ loadingProgress.currentLevel }}
          </span>
        </div>
      </div>
      
      <div class="loading-spinner" *ngIf="isLoading && !isRendering">
        <div class="spinner"></div>
      </div>
    </div>
  </div>

  <!-- Error State -->
  <div *ngIf="errorMessage && !isLoading" class="error-state">
    <div class="error-icon">⚠️</div>
    <h2>Error</h2>
    <p>{{ errorMessage }}</p>
    <button class="btn btn-primary" (click)="goBack()">Go Back</button>
  </div>

  <!-- Timeline Content -->
  <div *ngIf="timeline && !isLoading" class="timeline-content">
    <!-- Header -->
    <div class="timeline-header">
      <button class="back-btn" (click)="goBack()" title="Go back">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>Back</span>
      </button>
      <div class="header-content">
        <h1>Family Timeline</h1>
        <h2 class="root-relative-name">Starting from {{ timeline.rootRelativeName }}</h2>
        <div class="header-controls">
          <div class="level-selector">
            <label for="level-select">Family Levels:</label>
            <select id="level-select" [(ngModel)]="selectedLevel" (change)="onLevelChange()" class="level-dropdown">
              <option [value]="1">1 Level (Parents, Siblings, Children)</option>
              <option [value]="2">2 Levels (Grandparents, Parents, Siblings, Children, Grandchildren)</option>
            </select>
          </div>
          <p class="family-stats">{{ timeline.totalFamilyMembers }} family member{{ timeline.totalFamilyMembers !== 1 ? 's' : '' }} • {{ displayedEvents.length || timeline.events.length }} event{{ (displayedEvents.length || timeline.events.length) !== 1 ? 's' : '' }}</p>
        </div>
      </div>
    </div>

    <!-- Timeline -->
    <div class="timeline-wrapper">
      <div class="timeline-line"></div>
      
      <!-- Progressively rendered events -->
      <div class="timeline-events" *ngIf="displayedEvents.length > 0">
        <div 
          *ngFor="let event of displayedEvents; let i = index" 
          class="timeline-event fade-in"
          [style.animation-delay]="(i * 30) + 'ms'"
          [style.--event-color]="getEventColor(event.eventType)"
          [style.--relative-color]="getRelativeColor(event.relativeName)">
          <div class="event-marker">
            <div class="event-icon">{{ getEventTypeIcon(event.eventType) }}</div>
            <div class="relative-indicator" [style.background-color]="getRelativeColor(event.relativeName)"></div>
          </div>
          
          <div class="event-content">
            <div class="event-card">
              <div class="event-header">
                <div class="event-title-section">
                  <h3 class="event-title">{{ event.title || event.eventType }}</h3>
                  <div class="badge-group">
                    <span class="relative-badge" [style.background-color]="getRelativeColor(event.relativeName)">
                      {{ event.relativeName }}
                    </span>
                    <span class="level-badge" [class.level-root]="event.level === 0" 
                          [class.level-up]="event.level < 0" 
                          [class.level-down]="event.level > 0"
                          [title]="getLevelDescription(event.level)">
                      {{ getLevelLabel(event.level) }}
                    </span>
                  </div>
                </div>
                <span class="event-date">{{ formatDate(event.eventDate) }}</span>
              </div>
              
              <div class="event-body">
                <p class="event-description" *ngIf="event.description">
                  {{ event.description }}
                </p>
                
                <div class="event-details">
                  <div class="event-type-badge" [style.background-color]="getEventColor(event.eventType)">
                    {{ event.eventType }}
                  </div>
                  
                  <div class="event-location" *ngIf="event.location">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" stroke-width="2"/>
                      <circle cx="12" cy="10" r="3" stroke="currentColor" stroke-width="2"/>
                    </svg>
                    {{ event.location }}
                  </div>
                  
                  <div class="event-related" *ngIf="event.relatedRelativeName">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    {{ event.relatedRelativeName }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Skeleton loaders while rendering -->
      <div *ngIf="isRendering && displayedEvents.length < loadingProgress.total" 
           class="skeleton-events">
        <div *ngFor="let i of getRemainingEventsCount()" 
             class="skeleton-event">
          <div class="skeleton-marker">
            <div class="skeleton-icon"></div>
          </div>
          <div class="skeleton-content">
            <div class="skeleton-line title"></div>
            <div class="skeleton-line short"></div>
            <div class="skeleton-line"></div>
          </div>
        </div>
      </div>
      
      <div class="no-events" *ngIf="!isLoading && !isRendering && displayedEvents.length === 0 && timeline">
        <p>No timeline events available for this family.</p>
      </div>
    </div>
  </div>
</div>

